import React, { ComponentProps, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TouchableOpacity } from 'react-native';

import RadioButton from '@components/RadioButton/RadioButton';
import { Box, ShadowBox, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { TaskSortOption } from '@graphql/generated';
import { useCloseOnOutsideClick } from '@hooks/useOutsideClick.web';

import theme from '../../../themes/theme';

type PositionTuple = ['left' | 'right', 'top' | 'bottom'];

type Position = PositionTuple | 'top' | 'bottom' | 'left' | 'right';

// Use string literals that match TaskSortOption enum values to avoid
// issues with enum being undefined in test environments
export type SortOptionValue =
  | 'DUE_DATE_ASC'
  | 'DUE_DATE_DESC'
  | 'UPDATED_AT_DESC'
  | 'PRIORITY_ASC'
  | 'PRIORITY_DESC'
  | 'UPDATED_AT_ASC'
  | 'CREATED_AT_ASC'
  | 'CREATED_AT_DESC'
  | undefined;

// Map string values to TaskSortOption enum for use with GraphQL queries
export const sortValueToTaskSortOption = (
  value: SortOptionValue
): TaskSortOption | undefined => {
  if (!value) return undefined;
  switch (value) {
    case 'DUE_DATE_ASC':
      return TaskSortOption.DueDateAsc;
    case 'DUE_DATE_DESC':
      return TaskSortOption.DueDateDesc;
    case 'UPDATED_AT_ASC':
      return TaskSortOption.UpdatedAtAsc;
    case 'UPDATED_AT_DESC':
      return TaskSortOption.UpdatedAtDesc;
    case 'CREATED_AT_ASC':
      return TaskSortOption.CreatedAtAsc;
    case 'CREATED_AT_DESC':
      return TaskSortOption.CreatedAtDesc;
    case 'PRIORITY_ASC':
      return TaskSortOption.PriorityAsc;
    case 'PRIORITY_DESC':
      return TaskSortOption.PriorityDesc;
    default:
      return undefined;
  }
};

type Props = Omit<ComponentProps<typeof TouchableOpacity>, 'onPress'> & {
  title: string;
  onDismiss?: () => void;
  selectedSort?: SortOptionValue;
  onSelectSort?: (value: SortOptionValue) => void;
  boxProps: Partial<{
    position: Position;
    offset: [number, number] | number;
  }>;
  isOpen?: boolean;
  onToggle?: (open: boolean) => void;
};

type SortGroup = {
  label: string;
  options: {
    label: string;
    value: SortOptionValue;
    supported: boolean;
  }[];
};

const SORT_GROUPS: SortGroup[] = [
  {
    label: 'Due Date',
    options: [
      { label: 'Soonest', value: 'DUE_DATE_ASC', supported: true },
      { label: 'Latest', value: 'DUE_DATE_DESC', supported: true },
    ],
  },
  {
    label: 'Priority',
    options: [
      { label: 'Low to High', value: 'PRIORITY_ASC', supported: true },
      { label: 'High to Low', value: 'PRIORITY_DESC', supported: true },
    ],
  },
  {
    label: 'Last Updated',
    options: [
      {
        label: 'Newest',
        value: 'UPDATED_AT_DESC',
        supported: true,
      },
      { label: 'Oldest', value: 'UPDATED_AT_ASC', supported: true },
    ],
  },
  {
    label: 'Created Date',
    options: [
      { label: 'Newest', value: 'CREATED_AT_DESC', supported: true },
      { label: 'Oldest', value: 'CREATED_AT_ASC', supported: true },
    ],
  },
];

export const PopupTaskSortButton: React.FC<Props> = ({
  onDismiss: _onDismiss,
  boxProps: { position, offset },
  title,
  selectedSort,
  onSelectSort,
  isOpen,
  onToggle,
}) => {
  const [internalVisible, setInternalVisible] = useState(false);
  const [_mouseOver, setMouseOver] = useState(false);
  const [hoveredOption, setHoveredOption] = useState<string | undefined>();

  const visible = isOpen ?? internalVisible;

  const togglePopup = () => {
    const next = !visible;
    onToggle ? onToggle(next) : setInternalVisible(next);
  };

  const popupRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  const buttonRef = useCloseOnOutsideClick<HTMLDivElement>(
    visible,
    () => {
      onToggle ? onToggle(false) : setInternalVisible(false);
    },
    { extraRefs: [popupRef] }
  );

  const [layout, setLayout] = useState<
    { width: number; height: number } | undefined
  >();

  const adjustBounds = (
    positionArg: Props['boxProps']['position'] = 'bottom',
    offsetArg: Props['boxProps']['offset'] = 0,
    client: DOMRect,
    popupLayout: { width: number; height: number }
  ) => {
    const [offsetLeftRight, offsetTopBottom] =
      typeof offsetArg === 'number' ? [offsetArg, offsetArg] : offsetArg;

    const normalizedPosition = (typeof positionArg !== 'string' &&
      positionArg) ||
      ((positionArg === 'left' || positionArg === 'right') && [
        positionArg,
        'bottom',
      ]) ||
      ((positionArg === 'top' || positionArg === 'bottom') && [
        'left',
        positionArg,
      ]) || ['left', 'bottom'];

    const [leftRight, topBottom] = normalizedPosition;
    const bounds = {
      top:
        topBottom === 'top'
          ? client.top - popupLayout.height - offsetTopBottom
          : client.top + client.height + offsetTopBottom,
      left:
        leftRight === 'left'
          ? client.left + client.width - popupLayout.width - offsetLeftRight
          : client.left + offsetLeftRight,
    };

    return bounds;
  };

  const adjustedBounds =
    ref.current &&
    layout &&
    adjustBounds(position, offset, ref.current.getBoundingClientRect(), layout);

  const backgroundColor1 = () => (visible || _mouseOver ? 'grey01' : 'white');

  const handleSelectSort = (value: SortOptionValue, supported: boolean) => {
    if (supported) {
      onSelectSort?.(value);
    }
    // For unsupported options, do nothing (empty function as requested)
  };

  const handleClearAll = () => {
    onSelectSort?.(undefined);
  };

  const renderSortMenu = () => (
    <ShadowBox
      width={400}
      borderRadius='xs'
      variant='base'
      backgroundColor='white'>
      {SORT_GROUPS.map((group, groupIndex) => (
        <Box key={group.label}>
          {/* Section header with top border for all sections except first */}
          <Box
            paddingVertical='xs'
            paddingHorizontal='m'
            borderTopWidth={groupIndex > 0 ? 1 : 0}
            borderTopColor='grey01'
            borderBottomWidth={1}
            borderBottomColor='grey01'>
            <Text variant='labelEmphasized' color='textPrimary'>
              {group.label}
            </Text>
          </Box>
          {/* Options for this section */}
          {group.options.map((option) => {
            const optionKey = `${group.label}-${option.label}`;
            const isSelected = selectedSort === option.value;
            return (
              <Box
                key={optionKey}
                style={{
                  paddingVertical: 9,
                  backgroundColor:
                    hoveredOption === optionKey
                      ? theme.colors.grey01
                      : theme.colors.white,
                }}
                onMouseEnter={() => setHoveredOption(optionKey)}
                onMouseLeave={() => setHoveredOption(undefined)}>
                <TouchableOpacity
                  onPress={() =>
                    handleSelectSort(option.value, option.supported)
                  }
                  accessibilityLabel={`${group.label} ${option.label}`}>
                  <Box
                    flexDirection='row'
                    alignItems='center'
                    paddingHorizontal='m'>
                    <RadioButton
                      isSelected={isSelected}
                      onPress={() =>
                        handleSelectSort(option.value, option.supported)
                      }
                      iconVariant='m'
                    />
                    <Text
                      marginLeft='xs'
                      variant='webBodySecondary'
                      color='textPrimary'>
                      {option.label}
                    </Text>
                  </Box>
                </TouchableOpacity>
              </Box>
            );
          })}
        </Box>
      ))}
      {/* Clear All button with top border */}
      <TouchableOpacity
        onPress={handleClearAll}
        accessibilityLabel='Clear All'
        disabled={!selectedSort}>
        <Box
          alignItems='center'
          borderTopWidth={1}
          borderTopColor='grey02'
          backgroundColor='white'></Box>
        <Box
          paddingVertical='m'
          alignItems='center'
          borderTopWidth={1}
          borderTopColor='grey01'
          backgroundColor='white'>
          <Text
            variant='webBodySecondary'
            color={selectedSort ? 'textPrimary' : 'grey03'}>
            Clear All
          </Text>
        </Box>
      </TouchableOpacity>
    </ShadowBox>
  );

  const bounds = adjustedBounds ?? { left: 0, top: 0 };

  // Count of selected sort options (0 or 1 since sort is single-select)
  const sortCount = selectedSort ? 1 : 0;

  return (
    <Box ref={ref}>
      <Box
        ref={buttonRef}
        flexDirection='row'
        alignItems='center'
        paddingVertical='xs'
        paddingHorizontal='m'
        borderRadius='xs'
        onMouseEnter={() => {
          setMouseOver(true);
        }}
        onMouseLeave={() => {
          setMouseOver(false);
        }}
        backgroundColor={backgroundColor1()}>
        <TouchableOpacity
          accessibilityLabel='Sort'
          onPress={() => {
            togglePopup();
          }}>
          <Box flexDirection='row' alignItems='center'>
            <Icon variant='s' name='Sort' color='textSecondary' />
            <Text ml='xs' variant='webBody' color='textPrimary'>
              {title}
            </Text>
          </Box>
        </TouchableOpacity>
        {sortCount > 0 && (
          <>
            <Box
              backgroundColor='greenSecondary'
              borderRadius='xl'
              width={20}
              height={20}
              alignItems='center'
              justifyContent='center'
              marginLeft='xs'>
              <Text variant='metadata' color='white'>
                {sortCount}
              </Text>
            </Box>
            <TouchableOpacity
              onPress={() => {
                handleClearAll();
              }}
              style={{ marginLeft: 8 }}>
              <Icon variant='s' name='X' color='textPrimary' />
            </TouchableOpacity>
          </>
        )}
      </Box>

      {!layout && (
        <Box
          position='absolute'
          left='-200vw'
          visible={!layout}
          onLayout={({ nativeEvent: { layout: layoutEvent } }) => {
            setLayout((curr) => curr ?? layoutEvent);
          }}>
          {renderSortMenu()}
        </Box>
      )}
      {visible &&
        createPortal(
          <Box
            ref={popupRef}
            position='absolute'
            style={{
              left: bounds.left,
              top: bounds.top,
              zIndex: 9999,
            }}
            width={layout?.width}>
            <Box flexShrink={1}>{renderSortMenu()}</Box>
          </Box>,
          document.body
        )}
    </Box>
  );
};
